import classNames from 'classnames';
import { FC, useState } from 'react';

import { Button, Heading, Icon, IconSvg, Input } from '@/components';
import { useRefinementList } from '@/opensearch';

import { RefinementListItem } from './RefinementList.components';
import { ordenFilterItems } from './util';

import styles from './RefinementList.module.scss';

const INITIAL_ITEMS_TO_SHOW = 10;

interface RefinementListProps {
  attribute: string;
  className: string;
  configValue?: string;
  hasSearchBar?: boolean;
  hasScrollBar?: boolean;
  searchBarPlaceholder?: string;
  isChipsStyle?: boolean;
  isTwoColumns?: boolean;
  name?: string;
}

const RefinementList: FC<RefinementListProps> = ({
  attribute,
  className,
  hasSearchBar = false,
  hasScrollBar = false,
  searchBarPlaceholder,
  isChipsStyle = false,
  isTwoColumns = false,
  name,
}) => {
  const { items, refine } = useRefinementList({ attribute });
  const [searchValue, setSearchValue] = useState('');
  const [moreCount, setMoreCount] = useState(1);

  const showedItemsCount = moreCount * INITIAL_ITEMS_TO_SHOW;
  const hasReachedLimit = showedItemsCount > items?.length;

  // Split items into checked and unchecked arrays
  const checkedItems = items.filter(({ isRefined }) => isRefined);
  const uncheckedItems = items.filter(({ isRefined }) => !isRefined);

  // Filter unchecked items based on the search input
  const filteredUncheckedItems = uncheckedItems.filter(({ value }) =>
    JSON.stringify(value)?.toLowerCase()?.includes(searchValue.toLowerCase()),
  );

  const checkedSorted = ordenFilterItems(checkedItems, attribute);
  const uncheckedSorted = ordenFilterItems(filteredUncheckedItems, attribute);

  // Concatenate the checked array followed by the unchecked array
  const orderedItems = [...checkedSorted, ...uncheckedSorted];
  const hasMoreItems = orderedItems.length > INITIAL_ITEMS_TO_SHOW;

  const handleShowMore = () => {
    setMoreCount(prev => prev + 1);
  };

  const handleShowLess = () => {
    setMoreCount(1);
  };

  const itemsToShow = hasScrollBar
    ? orderedItems
    : orderedItems?.slice(0, showedItemsCount);

  if (orderedItems?.length === 0 && !searchValue) {
    return null;
  }

  // split into two columns when isTwoColumns is true
  const itemsPerColumn = isTwoColumns
    ? Math.ceil((itemsToShow?.length ?? 0) / 2)
    : 0;

  const leftColumnItems = isTwoColumns
    ? itemsToShow.slice(0, itemsPerColumn)
    : [];
  const rightColumnItems = isTwoColumns
    ? itemsToShow.slice(itemsPerColumn)
    : [];

  const renderItem = ({ value, isRefined, count, label }: any) => (
    <RefinementListItem
      className={classNames({
        [styles.filters_list_item__pills]: isChipsStyle,
      })}
      count={count}
      handleChange={() => refine(value)}
      isChecked={isRefined}
      key={`refine_${String(value)}`}
      label={label}
      value={value}
    />
  );

  return (
    <div className={classNames(className, styles.filters_list)}>
      {hasSearchBar && (
        <>
          <div className={styles.search_box}>
            <Input
              autoComplete="off"
              className={styles.search_box__input}
              isSmall
              name={`${attribute}-search-input`}
              onChange={e => setSearchValue(e.target.value)}
              placeholder={searchBarPlaceholder}
              type="search"
              value={searchValue}
            />
            <Button
              ariaLabel={name ? `Search ${name}` : 'Search filters'}
              className={styles.search_box__button}
              color="primary"
              onClick={() => {}}
              type="button"
            >
              <IconSvg decorative name="search" />
            </Button>
          </div>
          {orderedItems?.length === 0 && (
            <div className={styles.input_feedback}>
              <Icon name="search" />
              <Heading level={5}>No search results found</Heading>
              <p>No results match the criteria</p>
            </div>
          )}
        </>
      )}
      <div
        className={classNames({
          [styles.filters_list__with_scrollbar]: hasScrollBar,
          [styles.filters_list__with_spacing]: isChipsStyle,
          [styles.filters_list__two_columns]: isTwoColumns,
        })}
      >
        {isTwoColumns ? (
          <>
            <div className={styles.filters_list__column}>
              {leftColumnItems.map(renderItem)}
            </div>
            <div className={styles.filters_list__column}>
              {rightColumnItems.map(renderItem)}
            </div>
          </>
        ) : (
          itemsToShow?.map(renderItem)
        )}
      </div>
      {!hasScrollBar && hasMoreItems && (
        <button
          className={styles.show_more}
          onClick={hasReachedLimit ? handleShowLess : handleShowMore}
        >
          {`${hasReachedLimit ? '- Less' : '+ More'} ${name}`}
        </button>
      )}
    </div>
  );
};

export { RefinementList };
